/**
 * AuthContext — Cognito authentication for organizers.
 *
 * Tokens are stored in localStorage. On mount the context decodes the
 * saved access token, checks expiry, and silently refreshes if needed.
 * getToken() is the safe accessor used by AwsRestService — it refreshes
 * automatically when within 5 minutes of expiry.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

const API_URL     = import.meta.env.VITE_API_URL as string;
const TOKEN_KEY   = 'rp_access_token';
const REFRESH_KEY = 'rp_refresh_token';
const REFRESH_BUFFER_SECS = 300; // refresh when < 5 min remaining

// ── JWT helpers ───────────────────��──────────────────────────��─────────────

function decodeJwt(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    const base64  = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function tokenExpiry(token: string): number {
  return (decodeJwt(token)['exp'] as number | undefined) ?? 0;
}

function tokenIsValid(token: string | null): token is string {
  if (!token) return false;
  return tokenExpiry(token) > Date.now() / 1000 + REFRESH_BUFFER_SECS;
}

// ── Context shape ─────────────────���─────────────────────────────────���──────

export interface Organizer {
  sub:   string;
  email: string;
  plan:  'free' | 'pro';
}

interface AuthContextValue {
  organizer:    Organizer | null;
  accessToken:  string | null;
  isLoading:    boolean;
  login:         (email: string, password: string) => Promise<void>;
  signup:        (email: string, password: string, captchaToken: string) => Promise<void>;
  confirmEmail:  (email: string, code: string)     => Promise<void>;
  resendCode:    (email: string)                   => Promise<void>;
  forgotPassword:(email: string)                   => Promise<void>;
  resetPassword: (email: string, code: string, password: string) => Promise<void>;
  logout:        () => void;
  getToken:      () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── API helpers ──────────────────────────────────────────────────���─────────

async function authPost<T = unknown>(path: string, body: Record<string, string>): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json() as { error?: string } & T;
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed: ${res.status}`);
  return data;
}

// ── Provider ───────────────────────────────���────────────────────��──────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [organizer,   setOrganizer]   = useState<Organizer | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading,   setIsLoading]   = useState(true);

  // Fetch plan from /account and merge into organizer state
  const fetchPlan = useCallback(async (token: string, base: Omit<Organizer, 'plan'>) => {
    try {
      const res  = await fetch(`${API_URL}/account`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json() as { plan?: 'free' | 'pro' };
      setOrganizer({ ...base, plan: data.plan ?? 'free' });
    } catch {
      setOrganizer({ ...base, plan: 'free' });
    }
  }, []);

  // Persist tokens and derive organizer from the access token claims
  const applyTokens = useCallback((access: string, refresh: string) => {
    localStorage.setItem(TOKEN_KEY,   access);
    localStorage.setItem(REFRESH_KEY, refresh);
    setAccessToken(access);
    const claims = decodeJwt(access);
    const base = {
      sub:   claims['sub']   as string,
      email: (claims['email'] ?? claims['username'] ?? '') as string,
    };
    setOrganizer({ ...base, plan: 'free' });
    fetchPlan(access, base);
  }, [fetchPlan]);

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setAccessToken(null);
    setOrganizer(null);
  }, []);

  // Refresh using the stored refresh token; throws if it fails
  const doRefresh = useCallback(async (refreshToken: string): Promise<string> => {
    const data = await authPost<{ accessToken: string; idToken: string }>(
      '/auth/refresh',
      { refreshToken },
    );
    const newAccess = data.accessToken;
    localStorage.setItem(TOKEN_KEY, newAccess);
    setAccessToken(newAccess);
    const claims = decodeJwt(newAccess);
    const base = {
      sub:   claims['sub']   as string,
      email: (claims['email'] ?? claims['username'] ?? '') as string,
    };
    setOrganizer({ ...base, plan: 'free' });
    fetchPlan(newAccess, base);
    return newAccess;
  }, []);

  // On mount: restore session from localStorage
  useEffect(() => {
    (async () => {
      try {
        const saved        = localStorage.getItem(TOKEN_KEY);
        const savedRefresh = localStorage.getItem(REFRESH_KEY);

        if (saved && tokenIsValid(saved)) {
          setAccessToken(saved);
          const claims = decodeJwt(saved);
          const base = {
            sub:   claims['sub']   as string,
            email: (claims['email'] ?? claims['username'] ?? '') as string,
          };
          setOrganizer({ ...base, plan: 'free' });
          await fetchPlan(saved, base);
        } else if (savedRefresh) {
          await doRefresh(savedRefresh);
        }
      } catch {
        clearSession();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [doRefresh, clearSession]);

  // ── Public API ──────────���──────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string) => {
    const data = await authPost<{ accessToken: string; refreshToken: string; idToken: string }>(
      '/auth/login',
      { email, password },
    );
    applyTokens(data.accessToken, data.refreshToken);
  }, [applyTokens]);

  const signup = useCallback(async (email: string, password: string, captchaToken: string) => {
    await authPost('/auth/signup', { email, password, captchaToken });
  }, []);

  const confirmEmail = useCallback(async (email: string, code: string) => {
    await authPost('/auth/confirm', { email, code });
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await authPost('/auth/resend', { email });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await authPost('/auth/forgot-password', { email });
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, password: string) => {
    await authPost('/auth/confirm-forgot-password', { email, code, password });
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  /**
   * Returns a valid access token, refreshing silently if needed.
   * Used by AwsRestService before every request.
   */
  const getToken = useCallback(async (): Promise<string> => {
    const current = localStorage.getItem(TOKEN_KEY);
    if (current && tokenIsValid(current)) return current;

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) throw new Error('Not authenticated');

    try {
      return await doRefresh(refreshToken);
    } catch {
      clearSession();
      throw new Error('Session expired — please log in again');
    }
  }, [doRefresh, clearSession]);

  return (
    <AuthContext.Provider value={{
      organizer, accessToken, isLoading,
      login, signup, confirmEmail, resendCode,
      forgotPassword, resetPassword, logout, getToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ─────────────────────────���───────────────────────��─────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
